import React, { useState, useRef, useEffect } from "react";
import { Send, ArrowLeft } from "lucide-react";
import { Message } from "./chat.types";
import { cn } from "@/lib/utils";

interface ChatConversationProps {
  messages: Message[];
  onSendMessage: (text: string) => void;
  onBack: () => void;
  isProcessing?: boolean;
}

export function ChatConversation({ 
  messages, 
  onSendMessage, 
  onBack,
  isProcessing = false
}: ChatConversationProps) {
  const [inputValue, setInputValue] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isProcessing) return;
    onSendMessage(inputValue.trim());
    setInputValue("");
  };

  return (
    <div className="flex flex-col h-full bg-[#fdfcf6]">
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 scroll-smooth"
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              "max-w-[85%] rounded-2xl p-3 text-sm",
              msg.role === "customer"
                ? "bg-primary text-primary-foreground ml-auto rounded-tr-none"
                : "bg-white border border-primary/10 text-primary mr-auto rounded-tl-none shadow-sm"
            )}
          >
            {msg.text}
          </div>
        ))}
        {isProcessing && (
          <div className="bg-white border border-primary/10 text-primary mr-auto rounded-2xl rounded-tl-none p-3 shadow-sm max-w-[85%]">
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" />
              <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:0.2s]" />
              <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:0.4s]" />
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t border-primary/10 flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Ask anything..."
          disabled={isProcessing}
          className="flex-1 bg-white border border-primary/20 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!inputValue.trim() || isProcessing}
          className="bg-primary text-primary-foreground p-2 rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Send className="h-5 w-5" />
        </button>
      </form>
    </div>
  );
}